import {createUseMutation, createUseQuery} from "@irati/utils/services/apollo"
import {
  MutationClearProductSettingsRewardsArgs,
  MutationUpdateProductSettingsRewardsArgs,
  ProductSettings,
  QueryPermissionsProductSettingsForTeamArgs
} from "@irati/types"
import {gql} from "@irati/utils/gql"
import {Asserts} from "@shared/utils"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {match, P} from "ts-pattern"
import productSettingsTeamQuery, {productSettingsTeamKey} from "../services/queries/productSettings"
import fullProductSettings from "../services/fragments/fullProductSettings"

export const useProductSettingsQuery = createUseQuery<ProductSettings, QueryPermissionsProductSettingsForTeamArgs>(
  productSettingsTeamQuery,
  {
    key: productSettingsTeamKey
  }
)

export const useUpdateProductSettingsRewardMutation = createUseMutation<ProductSettings, MutationUpdateProductSettingsRewardsArgs>(
  gql`
    mutation UpdateProductSettingsReward($businessId: ID!, $teamId: ID!, $data: UpdateProductSettingsRewards!) {
      updateProductSettingsRewards(businessId: $businessId, teamId: $teamId, data: $data) {
        ...fullProductSettings
      }
    }
    ${fullProductSettings}
  `,
  {
    key: "updateProductSettingsRewards"
  }
)

export const useClearProductSettingsRewardMutation = createUseMutation<ProductSettings, MutationClearProductSettingsRewardsArgs>(
  gql`
    mutation ClearProductSettingsRewards($businessId: ID!, $teamId: ID!) {
      clearProductSettingsRewards(businessId: $businessId, teamId: $teamId) {
        ...fullProductSettings
      }
    }
    ${fullProductSettings}
  `,
  {
    key: "clearProductSettingsRewards"
  }
)

export const useProductSettingsRewardQuery = () => {
  const auth = useAuth()
  const apollo = useProductSettingsQuery({
    businessId: Asserts.val(auth.business?.uuid),
    teamId: Asserts.val(auth.business?.rootTeamId)
  })
  return {
    ...apollo,
    data: match(apollo.data?.rewardSettings?.business)
      .with(P.nullish, () => undefined)
      .with(
        {__typename: "ProductSettingsRewardDefault"},
        ({defaultMin, defaultMax}) =>
          ({
            state: "default",
            min: defaultMin,
            max: defaultMax,
            minNonCents: safeDiv(defaultMin, 100),
            maxNonCents: safeDiv(defaultMax, 100)
          }) as const
      )
      .with(
        {__typename: "ProductSettingsRewardSet"},
        ({setMin, setMax}) =>
          ({
            state: "set",
            min: setMin,
            max: setMax,
            minNonCents: safeDiv(setMin, 100),
            maxNonCents: safeDiv(setMax, 100)
          }) as const
      )
      .exhaustive()
  }
}
function safeDiv<T extends number | undefined>(value: T, divisor: number): T {
  if (value === undefined) {
    return undefined as T
  }
  return (value / divisor) as T
}
